import memoizeOne from 'memoize-one'
import {CustomUserProfileField, FilterCondition} from '../../common/types'
import {ConditionOperator} from '../../data/workbench-api/generated/types'

export type Field = {
  name: string
  label: string
  type?: 'date' | 'text'
}

export const getFieldType = memoizeOne(
  (customFields: CustomUserProfileField[], field?: string) =>
    allProfileFields.find(f => f.name === field)?.type ??
    customFields.find(f => f.name === field)?.type ??
    'text'
)

export const builtInProfileFields: Field[] = [
  {name: 'atomic___id', label: 'Atomic ID'},
  {name: 'atomic___created', label: 'Atomic created', type: 'date'},
  {name: 'city', label: 'City'},
  {name: 'country', label: 'Country'},
  {name: 'email', label: 'Email'},
  {name: 'external_id', label: 'External ID'},
  {name: 'name', label: 'Name'},
  {name: 'phone', label: 'Phone'},
  {name: 'region', label: 'Region'},
  {name: 'timezone', label: 'Timezone'},
  {name: 'user_type', label: 'User type'}
]

export const builtInAggregateFields: Field[] = [
  {name: 'first_seen_at', label: 'First seen', type: 'date'},
  {name: 'last_seen_at', label: 'Last seen', type: 'date'},
  {name: 'last_ios_seen_at', label: 'Last seen on iOS', type: 'date'},
  {name: 'last_android_seen_at', label: 'Last seen on Android', type: 'date'},
  {name: 'last_browser_seen_at', label: 'Last seen on Web', type: 'date'},

  {name: 'last_android_sdk_version', label: 'Last Android SDK version'},
  {name: 'last_android_device', label: 'Last Android device'},
  {name: 'last_android_os_version', label: 'Last Android OS version'},
  {name: 'last_android_device_name', label: 'Last Android device name'},
  {
    name: 'last_android_sdk_client_app_version',
    label: 'Last Android SDK client app version'
  },

  {name: 'last_ios_sdk_version', label: 'Last iOS SDK version'},
  {name: 'last_ios_device', label: 'Last iOS device'},
  {name: 'last_ios_os_version', label: 'Last iOS OS version'},
  {name: 'last_ios_device_name', label: 'Last iOS device name'},
  {
    name: 'last_ios_sdk_client_app_version',
    label: 'Last iOS SDK client app version'
  },

  {name: 'last_browser', label: 'Last browser'},
  {name: 'last_browser_name', label: 'Last browser name'},
  {name: 'last_browser_version', label: 'Last browser version'},
  {name: 'last_browser_os', label: 'Last browser OS'},
  {name: 'last_browser_os_version', label: 'Last browser OS version'},
  {name: 'last_browser_sdk_version', label: 'Last web SDK version'}
]

export const getProfileFieldGroup = memoizeOne(
  (fieldName?: string): 'aggregate' | 'profile' => {
    const field = builtInAggregateFields.find(f => f.name === fieldName)
    return field ? 'aggregate' : 'profile'
  }
)

export const allProfileFields = builtInProfileFields.concat(
  builtInAggregateFields
)

const futureConditionOptions: {
  value: FilterCondition['condition']
  text: string
}[] = [
  {
    value: ConditionOperator.GreaterThanFutureInterval,
    text: 'After the next'
  },
  {
    value: ConditionOperator.WithinFutureInterval,
    text: 'In the next'
  }
]

export const getConditionOptions = memoizeOne(
  (
    fieldType: 'date' | 'text' | undefined,
    // e.g. custom profile fields might be future, but events can't be
    allowFutureDates = false
  ): {
    value: FilterCondition['condition']
    text: string
  }[] =>
    fieldType === 'date'
      ? [
          {
            value: ConditionOperator.GreaterThanInterval,
            text: 'Before the last'
          },
          {value: ConditionOperator.LessThanInterval, text: 'In the last'},
          ...(allowFutureDates ? futureConditionOptions : []),
          {value: ConditionOperator.LessThan, text: 'Before'},
          {value: ConditionOperator.GreaterThan, text: 'After'},
          {value: ConditionOperator.Equals, text: 'Equals'},
          {value: ConditionOperator.NotEquals, text: "Doesn't equal"},
          {value: ConditionOperator.IsEmpty, text: 'Is empty'},
          {value: ConditionOperator.NotEmpty, text: 'Not empty'}
        ]
      : [
          {value: ConditionOperator.Equals, text: 'Equals'},
          {value: ConditionOperator.NotEquals, text: "Doesn't equal"},
          {value: ConditionOperator.RegexMatch, text: 'Contains'},
          {value: ConditionOperator.NotRegexMatch, text: "Doesn't contain"},
          {value: ConditionOperator.IsEmpty, text: 'Is empty'},
          {value: ConditionOperator.NotEmpty, text: 'Not empty'},
          {value: ConditionOperator.GreaterThan, text: 'Is greater than'},
          {
            value: ConditionOperator.GreaterThanEqual,
            text: 'Is greater than or equal to'
          },
          {value: ConditionOperator.LessThan, text: 'Is less than'},
          {
            value: ConditionOperator.LessThanEqual,
            text: 'Is less than or equal to'
          },
          {value: ConditionOperator.OneOf, text: 'Is one of'},
          {value: ConditionOperator.NotOneOf, text: 'Is not one of'}
        ]
)

export const isFutureCondition = (condition?: FilterCondition['condition']) =>
  condition ? futureConditionOptions.some(c => c.value === condition) : false
